import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './OnboardingTour.module.css'
import { setPref } from '../utils/userPreferences'

const TOUR_COMPLETED_KEY = 'onboarding_tour_completed'

interface TourStep {
  title: string
  description: string
  target?: string // CSS selector for highlighting
  action?: string // Action button text
  route?: string // Route to navigate to
}

const TOUR_STEPS: TourStep[] = [
  {
    title: 'Welcome to Reppr!',
    description: 'Let\'s take a quick tour to help you get started managing your sports agency.',
  },
  {
    title: 'Add Your Clients',
    description: 'Start by adding your current clients. Click "New Client" to add player information, contracts, and performance data.',
    action: 'Go to Clients',
    route: '/clients',
  },
  {
    title: 'Track Prospects',
    description: 'Keep track of potential signings in the Prospects section. Add scouting notes, draft projections, and recruiting status.',
    action: 'View Prospects',
    route: '/prospects',
  },
  {
    title: 'Manage Deals',
    description: 'Track endorsements, sponsorships, NIL, and other deals from initial contact to execution. Use the pipeline view to see deal progress at a glance.',
    action: 'View Deals',
    route: '/deals',
  },
  {
    title: 'Stay Compliant',
    description: 'Never miss a deadline with compliance tracking. Set up reminders for certifications, filings, and important dates.',
    action: 'View Compliance',
    route: '/compliance',
  },
  {
    title: 'Connect your AI',
    description: 'Reppr holds the record; your own AI does the thinking. Add https://www.reppr.io/api/mcp as a connector in Claude, sign in with this account, and you can ask about your book and log work by typing a sentence. Nothing you store here is processed by AI unless you connect one.',
    action: 'Open Settings',
    route: '/settings',
  },
  {
    title: 'You\'re All Set!',
    description: 'That\'s the basics! Explore the dashboard for an overview of your agency, or check Settings to customize your experience.',
    action: 'Go to Dashboard',
    route: '/dashboard',
  },
]

interface OnboardingTourProps {
  onComplete?: () => void
}

export default function OnboardingTour({ onComplete }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // Check if tour was already completed
    const completed = localStorage.getItem(TOUR_COMPLETED_KEY)
    if (!completed) {
      // Small delay before showing
      const timer = setTimeout(() => setIsVisible(true), 500)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleNext = () => {
    const step = TOUR_STEPS[currentStep]
    if (step.route) {
      navigate(step.route)
    }

    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      handleComplete()
    }
  }

  const handleSkip = () => {
    handleComplete()
  }

  const handleComplete = () => {
    localStorage.setItem(TOUR_COMPLETED_KEY, 'true')
    setPref('tour_completed', true)
    setIsVisible(false)
    onComplete?.()
  }

  if (!isVisible) return null

  const step = TOUR_STEPS[currentStep]
  const isLastStep = currentStep === TOUR_STEPS.length - 1
  const _isFirstStep = currentStep === 0

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Progress indicator */}
        <div className={styles.progress}>
          {TOUR_STEPS.map((_, index) => (
            <div
              key={index}
              className={`${styles.progressDot} ${index === currentStep ? styles.active : ''} ${index < currentStep ? styles.completed : ''}`}
            />
          ))}
        </div>

        {/* Step content */}
        <div className={styles.content}>
          <h2 className={styles.title}>{step.title}</h2>
          <p className={styles.description}>{step.description}</p>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          {!isLastStep && (
            <button onClick={handleSkip} className={styles.skipButton}>
              Skip Tour
            </button>
          )}
          <button onClick={handleNext} className={styles.nextButton}>
            {step.action || (isLastStep ? 'Get Started' : 'Next')}
          </button>
        </div>

        {/* Step counter */}
        <div className={styles.counter}>
          Step {currentStep + 1} of {TOUR_STEPS.length}
        </div>
      </div>
    </div>
  )
}

// Hook to check if tour should be shown
export function useShouldShowTour(): boolean {
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_COMPLETED_KEY)
    setShouldShow(!completed)
  }, [])

  return shouldShow
}

// Function to reset tour (for testing or re-showing)
export function resetOnboardingTour() {
  localStorage.removeItem(TOUR_COMPLETED_KEY)
}
